import styles from "./AddressModal.module.css";
import { useEffect, useState } from "react";
import TextInput from "../forms/TextInput/TextInput";
import Flex from "../Flex/Flex";
import Select from "../forms/Select/Select";
import Button from "../forms/Button/Button";
import { Address, useUserContext } from "@/contexts/UserContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { createAddress, editAddress } from "@/api/addresses";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../Modal/Modal";
import { FormField, useForm } from "../Form";

export const StateOptions = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "District of Columbia", value: "DC" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

export interface AddressModalProps {
  open?: boolean;
  editingAddress?: Address;
  onClose?: (address?: Address) => void;
}

const defaultAddress: Address = {
  id: undefined,
  name: "",
  address1: "",
  address2: "",
  city: "",
  state: undefined,
  phone: "",
  zipcode: "",
};

export default function AddressModal(props: AddressModalProps) {
  const { pushNewAddress, updateAddress } = useUserContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const form = useForm<Address>(defaultAddress);

  const onClose = (address?: Address) => {
    props.onClose?.(address);
    setTimeout(() => setEditMode(true), 400);
  };

  useEffect(() => {
    if (props.open && props.editingAddress) {
      setEditMode(true);
      form.setValue({
        id: props.editingAddress.id ?? defaultAddress.id,
        name: props.editingAddress.name ?? defaultAddress.name,
        address1: props.editingAddress.address1 ?? defaultAddress.address1,
        address2: props.editingAddress.address2 ?? defaultAddress.address2,
        city: props.editingAddress.city ?? defaultAddress.city,
        state: props.editingAddress.state ?? defaultAddress.state,
        phone: props.editingAddress.phone ?? defaultAddress.phone,
        zipcode: props.editingAddress.zipcode ?? defaultAddress.zipcode,
      });
      form.resetState();
    }
  }, [props.editingAddress, props.open]);

  return (
    <Modal 
      open={props.open}
      onOpenChange={(open) => {
        if (!open && !loading) onClose();
      }}
    >
      <ModalHeader>
        <span className={"strong"}>{editMode ? "Edit Existing Address" : "Create Address"}</span>
      </ModalHeader>

      <ModalBody>
        <div className={styles.addressModal}>
          <form className={styles.createNewAddressForm}>
            <FormField
              form={form}
              name="name"
              displayName="Name"
              showLabel={true}
            >
              <TextInput
                inputId="name"
                placeholder="name"
                minLength={1}
                maxLength={255}
                invalid={form.state.name.invalid}
                value={form.value.name}
                onChange={e => form.setFieldValue("name", e)}
                required
              />
            </FormField>

            <FormField
              form={form}
              name="address1"
              displayName="Address 1"
              showLabel={true}
            >
              <TextInput
                inputId="address1"
                placeholder="address line 1"
                minLength={1}
                maxLength={255}
                invalid={form.state.address1.invalid}
                value={form.value.address1}
                onChange={e => form.setFieldValue("address1", e)}
                required
              />
            </FormField>

            <FormField
              form={form}
              name="address2"
              displayName="Address 2"
              showLabel={true}
              required={false}
            >
              <TextInput
                inputId="address2"
                placeholder="address line 2 (optional)"
                minLength={1}
                maxLength={255}
                invalid={form.state.address2.invalid}
                value={form.value.address2}
                onChange={e => form.setFieldValue("address2", e)}
              />
            </FormField>

            <FormField
              form={form}
              name="city"
              displayName="City"
              showLabel={true}
            >
              <TextInput
                inputId="city"
                placeholder="city"
                minLength={1}
                maxLength={255}
                invalid={form.state.city.invalid}
                value={form.value.city}
                onChange={e => form.setFieldValue("city", e)}
                required
              />
            </FormField>
        
            <FormField
              form={form}
              name="state"
              displayName="State"
              showLabel={true}
            >
              <Select   
                id="state"
                placeholder="select a state"                 
                fullWidth
                invalid={form.state.state.invalid}
                options={StateOptions}
                optionRender={(option) => {
                  return option.label;
                }}
                value={StateOptions.find(option => form.value.state === option.value)}
                onChange={e => form.setFieldValue("state", e.value)}
              />
            </FormField>

            <FormField
              form={form}
              name="zipcode"
              displayName="Zip code"
              showLabel={true}
              validator={value => value.match(/\d{5}(?:-\d{4})?/) ? null : "Zip code is invalid"}
            >
              <TextInput
                inputId="zipcode"
                placeholder="zip code"
                invalid={form.state.zipcode.invalid}
                minLength={5}
                maxLength={20}
                value={form.value.zipcode}
                onChange={e => form.setFieldValue("zipcode", e)}
                required
              />
            </FormField>

            <FormField
              form={form}
              name="phone"
              displayName="Phone"
              showLabel={true}
              validator={value => value.match(/^(\+1)?[()\-. ]*(\d[()\-. ]*){10}$/) ? null :  "Phone number is invalid"}
            >
              <TextInput
                inputId="phone"
                type="tel"
                invalid={form.state.phone.invalid}
                placeholder="10-digit phone number"
                minLength={10}
                maxLength={20}
                value={form.value.phone}
                onChange={e => form.setFieldValue("phone", e)}
                required
              />
            </FormField>

          </form>
        </div>
      </ModalBody>

      <ModalFooter>
        <Flex justify="space-between" align="center"> 
          <Flex align="center" gap={2}>
            <Button
              type="secondary"
              onClick={() => onClose()}
            >
              Cancel
            </Button>
            {editMode && (
              <Button
                type="secondary"
                disabled={loading}
                onClick={() => {
                  form.setValue({...defaultAddress});
                  form.resetState();
                  setEditMode(false);
                }}
              >
                Create a new Address Instead
              </Button>
            )}
          </Flex>
          <Flex justify="end" align="center" gap={2}>
            <Button
              disableWhileOnClick={true}
              onClick={async () => {
                if (!form.validate()) return;
                try {
                  setLoading(true);
                  if (editMode) {
                    await editAddress(form.value);
                    updateAddress(form.value);
                    onClose(form.value);
                  } else {
                    const result = await createAddress(form.value);
                    form.value.id = result.id;
                    pushNewAddress(form.value);
                    onClose(form.value);
                  }
                } catch(errors) {
                  handleErrorResponseGenerically(errors);
                } finally {
                  setLoading(false);
                }
              }}
            >
              {editMode ? "Update Address" : "Create new address"}
            </Button>
          </Flex>
        </Flex>
      </ModalFooter>
    </Modal>
  );
}
